import { HttpClient, HttpHeaders } from "@angular/common/http";
import { OnInit, AfterViewInit, Component, TemplateRef, ViewChild, ViewEncapsulation, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { HcModal, HcSort, HcTableDataSource, HcToastOptions, HcToasterService, ModalOptions, ModalService, PaginationComponent, SimpleModalOptions, TabChangeEvent } from "@healthcatalyst/cashmere";
import { Subject, filter, take, takeUntil } from "rxjs";
import { DownloadElement, DownloadService } from "src/app/shared/services/download.service";
import { OktaService } from "../../shared/services/okta.service";
import { environment } from '../../../environments/environment';

/** Admin download management */
@Component({
    selector: 'pd-admin-downloads',
    templateUrl: './admin-downloads.component.html',
    styleUrls: ['./admin-downloads.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminDownloadsComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<DownloadElement> = new HcTableDataSource();
    loading = true;
    loadError = false;
    displayedColumns: string[] = ['version', 'name', 'startDate', 'endDate', 'publishDate', 'actions'];
    pageSize: number = 10;
    currentTab = 'techlibrary';
    modalRef: HcModal<TemplateRef<any>>;

    adminDownloadForm = new FormGroup({
        name: new FormControl('', {nonNullable: true, validators: [Validators.required]}),
        version: new FormControl(0, {nonNullable: true, validators: [Validators.required]}),
        startDate: new FormControl(new Date(), {nonNullable: true, validators: [Validators.required]}),
        endDate: new FormControl(new Date(), {nonNullable: true, validators: [Validators.required]}),
        file: new FormControl(),
        publishDate: new FormControl(''),
        downloadID: new FormControl(0),
        azureID: new FormControl(''),
        fileType: new FormControl('', {nonNullable: true})
    });
 
    adminGenerateForm = new FormControl('', {nonNullable: true, validators: [Validators.required]});
    
    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get listLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor( 
        private http: HttpClient,
        private downloadService: DownloadService,
        private modalService: ModalService,
        private oktaService: OktaService,
        private toasterService: HcToasterService
    ) {}

    ngOnInit(): void {
        this.downloadService.downloadsUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.loading = false;
                this.loadError = false;

                let filteredList: DownloadElement[] = [];
                list.forEach(element => {
                    if ( element.type === this.currentTab ) {
                        filteredList.push( element );
                    }
                });

                this.dataSource = new HcTableDataSource(filteredList);
                if ( this.sort && this.paginator ) {
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;

                    setTimeout(() => {
                        if ( (this.sort.direction !== 'desc' || this.sort.active !== 'version') ) {
                            this.sort.sort( { disableClear: false, id: 'version', start: 'desc' } );
                        }
                    });
                }
            } else {
                this.loadError = this.downloadService.loadFailed;
                if ( !this.loadError ) {
                    this.downloadService.refresh();
                } else {
                    this.loading = false;
                }
            }
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        setTimeout(() => {
            this.sort.sort( { disableClear: false, id: 'version', start: 'desc' } );
        });
    }

    reloadDownloads(): void {
        this.downloadService.refresh();
    }

    tabClick(selected: TabChangeEvent) {
        const tabNames = ['techlibrary','metadata'];

        if ( this.downloadService.downloadsList.value ) {
            let filteredList: DownloadElement[] = [];
            this.downloadService.downloadsList.value.forEach(element => {
                if ( element.type === tabNames[selected.index] ) {
                    filteredList.push( element );
                }
            });

            this.dataSource = new HcTableDataSource(filteredList);
            if ( this.sort && this.paginator ) {
                this.dataSource.sort = this.sort;
                this.dataSource.paginator = this.paginator;
            }

            this.currentTab = tabNames[selected.index];
        }
    }

    generateDownload(type: string): void {
        if(type === 'techlibrary') {
            const name =  this.adminGenerateForm.value;
            const url = '/tech-catalog-generate?name=' + name;
            window.open(url, "_blank");
        } else {
            let headers = new HttpHeaders();
            headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);

            this.http.get(environment.azure.url + '/admin.' + type + '.generate', { responseType: 'blob', headers: headers })
                .subscribe({
                    next: (data) => {
                        const blob = new Blob([data], { type: 'text/csv' });
                        const url= window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.setAttribute('type', 'hidden');  
                        a.href = url;  
                        a.download = type + '.csv';  
                        a.click(); 
                        a.remove();
                    },
                    error: (msg) => {
                        console.log(msg);
                    }
                });
        }
    }

    addDownload(): void {
        if ( !this.adminDownloadForm.controls['file'].value ) {
            this.adminDownloadForm.controls['file'].markAsTouched();
            this.adminDownloadForm.controls['file'].setErrors({'valid': false});
        } else {
            const today = new Date();
            const tempDownload: DownloadElement = {
                downloadID: -1,
                type: this.currentTab,
                name: this.adminDownloadForm.controls['name'].value,
                azureID: '',
                publishDate: today.toISOString(),
                startDate: this.adminDownloadForm.controls['startDate'].value.toISOString(),
                endDate: this.adminDownloadForm.controls['endDate'].value.toISOString(),
                fileType: this.adminDownloadForm.controls['file'].value[0].type,
                version: this.adminDownloadForm.controls['version'].value
            };

            this.modalRef.close();

            this.downloadService.add( this.adminDownloadForm.controls['file'].value[0], tempDownload ).subscribe({
                next: () => {
                    const options: HcToastOptions = {
                        header: 'Download Added',
                        body: tempDownload.name + ' successfully added.',
                        type: 'success',
                        position: 'bottom-right'
                    };
                    this.toasterService.addToast(options);
                    this.downloadService.refresh();
                },
                error: (msg) => {
                    const options: HcToastOptions = {
                        header: 'Add Download Failed',
                        body: 'Unable to add download. Try again later.',
                        type: 'alert',
                        position: 'bottom-right'
                    };
                    this.toasterService.addToast(options);
                    console.log(msg);
                }
            });
        }
    }

    editDownload(): void {
        const tempDownload: DownloadElement = {
            downloadID: this.adminDownloadForm.controls['downloadID'].value,
            type: this.currentTab,
            name: this.adminDownloadForm.controls['name'].value,
            azureID: this.adminDownloadForm.controls['azureID'].value,
            publishDate: this.adminDownloadForm.controls['publishDate'].value,
            startDate: this.adminDownloadForm.controls['startDate'].value.toISOString(),
            endDate: this.adminDownloadForm.controls['endDate'].value.toISOString(),
            fileType: this.adminDownloadForm.controls['fileType'].value,
            version: this.adminDownloadForm.controls['version'].value
        };

        this.modalRef.close();

        this.downloadService.update( tempDownload ).subscribe({
            next: () => {
                const options: HcToastOptions = {
                    header: 'Download Updated',
                    body: tempDownload.name + ' successfully updated.',
                    type: 'success',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                this.downloadService.refresh();
            },
            error: (msg) => {
                const options: HcToastOptions = {
                    header: 'Update Download Failed',
                    body: 'Unable to update download. Try again later.',
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log(msg);
            }
        });
    }

    downloadElement( element: DownloadElement ): void {
        let headers = new HttpHeaders();
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);

        this.http.get(environment.azure.url + '/document?file=' + element.azureID, { responseType: 'blob', headers: headers }).subscribe({
            next: (data) => {
                const blob = new Blob([data], { type: element.fileType });
                const url= window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.setAttribute('type', 'hidden');  
                a.href = url;  
                a.download = element.type === 'techlibrary' ? element.name + '.pdf' : element.name + '.xlsx';  
                a.click(); 
                a.remove();
            },
            error: (msg) => {
                const options: HcToastOptions = {
                    header: 'Download Failed',
                    body: 'Unable to download file. Try again later.',
                    type: 'alert',
                    position: 'bottom-right'
                };
                this.toasterService.addToast(options);
                console.log(msg);
            }
        });
    }

    removeDownload( element: DownloadElement ): void {
        const options: SimpleModalOptions = {
            messageHTML: '<p>Are you sure you want to remove the download: <strong>' + element.name + '</strong>?<p>',
            headerText: 'Remove Download',
            confirmButtonText: 'Remove Download',
            icon: ''
        };
        this.modalService
            .confirmDestructive(options)
            .pipe(
                take(1),
                filter(confirmed => confirmed)
            )
            .subscribe(() => {
                this.downloadService.remove( element ).subscribe({
                    next: () => {
                        const options: HcToastOptions = {
                            header: 'Download Removed',
                            body: element.name + ' successfully removed.',
                            type: 'success',
                            position: 'bottom-right'
                        };
                        this.toasterService.addToast(options);
                        this.downloadService.refresh();
                    },
                    error: (msg) => {
                        const options: HcToastOptions = {
                            header: 'Remove Failed',
                            body: 'Unable to remove download. Try again later.',
                            type: 'alert',
                            position: 'bottom-right'
                        };
                        this.toasterService.addToast(options);
                        console.log(msg);
                    }
                });
            });
    }

    openInstructions(content: TemplateRef<any>): void {
        const options: ModalOptions = {
            size: 'lg',
            closeIcon: true
        };

        this.modalService.open(content, options);
    }

    openGenerateModal(content: TemplateRef<any>): void {
        const options: ModalOptions = {
            size: 'md',
            closeIcon: true,
            data: {
                header: 'Tech Library',
                type: 'techlibrary'
            }
        };

        this.modalService.open(content, options);
    }

    openAddEditModal(content: TemplateRef<any>, element?: DownloadElement ): void {
        const options: ModalOptions = {
            size: 'md',
            closeIcon: true,
            data: {
                edit: element ? true : false
            }
        };

        this.adminDownloadForm.patchValue({
            'name': element ? element.name : '',
            'version': element ? element.version : 0,
            'startDate': element ? new Date(element.startDate) : new Date(),
            'endDate': element ? new Date(element.endDate) : new Date(),
            'file': null,
            'publishDate': element ? element.publishDate : '',
            'downloadID': element ? element.downloadID : 0,
            'azureID': element ? element.azureID : '',
            'fileType': element ? element.fileType : ''
        });

        this.modalRef = this.modalService.open(content, options);
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}